/**
 * Lift type and maintenance status labels (#4096).
 *
 * Module-scope labelKey maps (CLAUDE.md): the keys live in the `equipment`
 * namespace, and callers render them through a `t` bound to that namespace —
 * `useTranslation('equipment')` — so the label follows the user's language
 * instead of a hardcoded English string.
 */

import type { LiftType, MaintenanceStatus } from '@/hooks/useLifts';

type Translate = (key: string) => string;

export const LIFT_TYPE_LABEL_KEYS: Readonly<Record<LiftType, string>> = {
  '2_post': 'lifts.type2Post',
  '4_post': 'lifts.type4Post',
  scissor: 'lifts.typeScissor',
  pit: 'lifts.typePit',
};

/** Lift types in display order, each with its `equipment`-namespace label key. */
export const LIFT_TYPE_OPTIONS: ReadonlyArray<{ value: LiftType; labelKey: string }> = (
  Object.keys(LIFT_TYPE_LABEL_KEYS) as LiftType[]
).map((value) => ({ value, labelKey: LIFT_TYPE_LABEL_KEYS[value] }));

export const MAINTENANCE_STATUS_LABEL_KEYS: Readonly<Record<MaintenanceStatus, string>> = {
  operational: 'lifts.statusOperational',
  needs_maintenance: 'lifts.statusNeedsMaintenance',
  out_of_service: 'lifts.statusOutOfService',
};

function hasKey<K extends string>(map: Readonly<Record<K, string>>, value: string): value is K {
  return Object.prototype.hasOwnProperty.call(map, value);
}

/**
 * Localized lift type. `t` must be bound to the `equipment` namespace.
 * An unknown type (newer server than client) is shown as-is, never as a raw key.
 */
export function liftTypeLabel(t: Translate, type: string): string {
  return hasKey(LIFT_TYPE_LABEL_KEYS, type) ? t(LIFT_TYPE_LABEL_KEYS[type]) : type;
}

/** Localized maintenance status. `t` must be bound to the `equipment` namespace. */
export function maintenanceStatusLabel(t: Translate, status: string): string {
  return hasKey(MAINTENANCE_STATUS_LABEL_KEYS, status)
    ? t(MAINTENANCE_STATUS_LABEL_KEYS[status])
    : status;
}
